import React, { useContext, useState } from "react";
import { MillDetailsReportContext, initialState } from "./MillDetailsReportContext";
import { IDetailsReportFilters } from "pages/Corporate/Locations/MillDashboards/DetailsReport/DetailsReportFilters/DetailsFiltersInterfaces";
import { IMillDetailsReportContextProps } from "./MillDetailsReportContextInterfaces";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { UsersEntity } from "entities/Users/UsersEntity";
import { IGetUserFiltersPreferencesQuery } from "api/queries/usersAPI";
import { FiltersPreferencesReportsEnum } from "constants/constants";
import { filterObjectProperties } from "utilities/utilities";
import { validMillDetailsReportDefaultFields } from "pages/Corporate/Locations/MillDashboards/DetailsReport/DetailsReportFilters/useDetailsReportFilters";
import { Alert, Snackbar } from "@mui/material";
import { useTranslation } from "react-i18next";

export const MillDetailsReportContextProvider: React.FC<IMillDetailsReportContextProps> = ({ children }) => {
    const { t } = useTranslation();
    const { currentUser } = useContext(CorporateContext);

    const [filters, setFilters] = useState(initialState.filters);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [userFiltersDefault, setUserFiltersDefault] = useState<IDetailsReportFilters | null>(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const { upsertOperation, loading: loadingUpsertDefault } = UsersEntity.UpsertUserFiltersDefaultPreference();
    const { removeOperation, loading: loadingRemoveDefault } = UsersEntity.RemoveUserFiltersDefaultPreference();

    const handleQueryCompletion = (data: IGetUserFiltersPreferencesQuery) => {
        const userFilterPreference = data.user_filter_preferences;
        if (userFilterPreference[0]) {
            const filtersDefault: IDetailsReportFilters = JSON.parse(userFilterPreference[0].filter_selection);
            setUserFiltersDefault(filtersDefault);
            setFilters((prev) => ({ ...prev, ...filtersDefault }));
        } else {
            setUserFiltersDefault(null);
        }
    };
    const { loading: loadingGetDefault, refetch } = UsersEntity.GetUserFiltersPreferences(
        handleQueryCompletion,
        FiltersPreferencesReportsEnum.MILL_DASHBOARD_DETAILS,
        currentUser?.user_id,
    );

    const handleFiltersChange = (value: IDetailsReportFilters) => {
        setFilters(value);
    };

    const handleShowFiltersChange = (value: boolean) => {
        setShowFilters(value);
    };

    const handleSetFiltersDefault = async (detailsFilters: IDetailsReportFilters) => {
        try {
            const validFilters = filterObjectProperties(detailsFilters, validMillDetailsReportDefaultFields);
            await upsertOperation({
                variables: {
                    userPreference: {
                        user_id: currentUser?.user_id,
                        report_id: FiltersPreferencesReportsEnum.MILL_DASHBOARD_DETAILS,
                        filter_selection: JSON.stringify(validFilters),
                    },
                },
            });
            await refetch();
            setSuccessMessage(t("locations.mill_dashboards.filters_saved"));
        } catch (error) {
            console.error(error);
            setErrorMessage(t("locations.mill_dashboards.filters_error"));
        }
    };

    const handleRemoveDefaultFilters = async () => {
        try {
            await removeOperation({
                variables: {
                    userId: currentUser?.user_id,
                    reportId: FiltersPreferencesReportsEnum.MILL_DASHBOARD_DETAILS,
                },
            });
            setUserFiltersDefault(null);
            setSuccessMessage(t("locations.mill_dashboards.filters_removed"));
        } catch (error) {
            console.error(error);
            setErrorMessage(t("locations.mill_dashboards.filters_error"));
        }
    };

    const providerValue = {
        filters,
        handleFiltersChange,
        showFilters,
        handleShowFiltersChange,
        handleSetFiltersDefault,
        handleRemoveDefaultFilters,
        userFiltersDefault,
        loadingDefaultOperations: loadingGetDefault || loadingRemoveDefault || loadingUpsertDefault,
    };

    return (
        <MillDetailsReportContext.Provider value={providerValue}>
            <Snackbar
                open={!!successMessage || !!errorMessage}
                autoHideDuration={2000}
                onClose={() => {
                    setErrorMessage("");
                    setSuccessMessage("");
                }}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity={successMessage ? "success" : "error"} sx={{ width: "100%" }}>
                    <span style={{ whiteSpace: "pre-line" }}>{successMessage || errorMessage}</span>
                </Alert>
            </Snackbar>
            {children}
        </MillDetailsReportContext.Provider>
    );
};
